import { useQuery } from '@tanstack/react-query';
import { usePrismAuth } from '@/lib/prism-bridge';
import { getAppVersions, type AppVersionState } from './versions';
import { queryKeys } from './queryKeys';

/**
 * Versions of one app (GET /dev-portal/apps/{appId}/versions), with a per-shard state map.
 * Org-scoped query key. Gated on `isReady` (token + org id both synced from Prism) and the
 * appId — firing before the org id lands would 400 and show an error/retry page.
 *
 * `excludeStates` hides versions sitting in those states on every shard (OCP-2203) and is part
 * of the query key, so each filter is fetched once and then served from cache. It is required
 * rather than defaulted: callers that render the same view must pass the same set or they will
 * quietly fan out twice instead of sharing one request, and a default hides that from them.
 */
export function useAppVersions(appId: string, excludeStates: readonly AppVersionState[]) {
  const { token, orgId, isReady } = usePrismAuth();
  // Sorted so callers listing the same states in a different order hit the same cache entry.
  // This also normalises the order of the outgoing query params, which is harmless: rivendell
  // parses excludeStates into a Set, so their order carries no meaning at either end.
  const states = [...excludeStates].sort();
  return useQuery({
    queryKey: queryKeys.apps.versions(orgId, appId, states),
    queryFn: () => getAppVersions({ token, orgId }, appId, states),
    enabled: isReady && Boolean(appId),
  });
}
